import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useMemo } from "react";
import SettingsLayout from "../../../components/SettingsLayout";
import LicenseTypeTile from "../../../components/licenseType/LicenseTypeTile";
import If from "../../../components/utils/If";
import { EventType, LicenseType } from "../../../schema";
import PrimaryButton from "../../../ui/PrimaryButton";
import Skeleton from "../../../ui/skeleton/Skeleton";
import { route } from "../../../urls";

interface GetLicenseTypesQuery {
  licenseTypes: LicenseType[];
  eventTypes: EventType[];
}

export default function LicenseTypesScene() {
  const { loading, data, error } = useQuery<GetLicenseTypesQuery>(query, {
    errorPolicy: "all",
  });

  const canDeleteTypes = (data && data?.licenseTypes.length > 1) || false;
  const types = useMemo(() => {
    const maps = new Map<string, LicenseType>();
    data?.licenseTypes.forEach((type) => {
      maps.set(type.id, type);
    });
    return maps;
  }, [data]);

  const header = () => (
    <>
      <h2 className="text-2xl text-gray-dark font-medium">License Types</h2>
      <If condition={!loading && !error}>
        <PrimaryButton className="text-sm" to={route("license_type_add")}>
          Add License Type
        </PrimaryButton>
      </If>
    </>
  );

  return (
    <SettingsLayout title="Configure license types" header={header()}>
      <If condition={loading}>
        <Skeleton className="w-4/5 mb-4" />
        <Skeleton className="w-2/5" />
      </If>
      <If condition={!!data}>
        <p className="pb-6">
          License types allow you to have more than one license per trainer, and
          use separate renewal processes for each license. Learn more
        </p>
        <div className="grid lg:grid-cols-2 lg:gap-4 gap-y-4">
          {data?.licenseTypes.map((licenseType) => (
            <LicenseTypeTile
              key={licenseType.id}
              licenseType={licenseType}
              types={types}
              canDelete={canDeleteTypes}
              numberOfEventTypes={data?.eventTypes.length || 0}
            />
          ))}
        </div>
      </If>
    </SettingsLayout>
  );
}

const query = gql`
  query GetLicenseTypesQuery {
    licenseTypes: licenseTypes {
      id
      name
      tags
      active
      renewalPeriod
      parentLicenseTypeId
      agreement {
        id
        version
        consent
        content
      }
      renewalNotifications {
        id
      }
      eventTypes {
        id
        name
      }
    }
    eventTypes: eventTypes {
      id
      name
    }
  }
`;
